import type { FlagType } from "@appchoose/flag";
import { Flag } from "@appchoose/flag";
import type { Country } from "@shopify/address";
import { matchSorter } from "match-sorter";
import * as React from "react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "~/components/ui/button";
import { cn } from "~/components/ui/cn";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
} from "~/components/ui/command";
import { ArrowDownIcon } from "~/components/ui/icons/arrow-down";
import { CheckIcon } from "~/components/ui/icons/check";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { ScrollArea } from "~/components/ui/scroll-area";
import { useCountriesQuery } from "~/hooks/use-countries-query";

const CountryItem: React.FC<{ country: Country }> = ({ country }) => {
  return (
    <div className="flex flex-1 items-center gap-2">
      <Flag flag={country.code as FlagType} className="size-6!" />

      <span className="text-sm font-medium">{country.name}</span>
    </div>
  );
};

export function CountryCombobox({
  disabled,
  onChange,
  value,
}: {
  disabled?: boolean;
  onChange: (value: string | undefined) => void;
  value: string | undefined;
}) {
  const { i18n, t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [open, setOpen] = useState(false);

  const listRef = useRef<HTMLDivElement>(null);
  const scrollId = useRef<number>(null);

  const { data: countries, isLoading } = useCountriesQuery({
    locale: i18n.language,
  });

  const countryMatches = React.useMemo(
    () => matchSorter(countries ?? [], searchTerm, { keys: ["name"] }),
    [countries, searchTerm],
  );

  const selectedValue = value
    ? countries?.find(
        (country) => country.code.toLowerCase() === value.toLowerCase(),
      )
    : undefined;

  return (
    <Popover modal={true} open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="rounded border"
          disabled={disabled}
        >
          {selectedValue ? (
            <CountryItem country={selectedValue} />
          ) : (
            t("country_combobox.placeholder")
          )}
          <ArrowDownIcon className="size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popper-anchor-width] p-0">
        <Command shouldFilter={false}>
          <CommandInput
            value={searchTerm}
            onValueChange={(searchTerm) => {
              setSearchTerm(searchTerm);

              //#region scroll list to top when typing
              // https://github.com/pacocoursey/cmdk/issues/234
              // https://github.com/pacocoursey/cmdk/issues/233

              // clear pending scroll
              if (scrollId.current) cancelAnimationFrame(scrollId.current);

              // the setTimeout is used to create a new task
              // this is to make sure that we don't scroll until the user is done typing
              // you can tweak the timeout duration ofc
              scrollId.current = requestAnimationFrame(() => {
                // inside your list select the first group and scroll to the top
                listRef.current?.scrollTo({ top: 0 });
              });

              //#endregion
            }}
            placeholder={t("country_combobox.search_placeholder")}
          />

          <CommandList>
            {isLoading ? (
              <CommandLoading>{t("country_combobox.loading")}</CommandLoading>
            ) : null}
            {!isLoading ? (
              <CommandEmpty>{t("country_combobox.no_result")}</CommandEmpty>
            ) : null}
            <CommandGroup>
              <ScrollArea className="max-h-48 overflow-scroll" ref={listRef}>
                {countryMatches.map((country) => (
                  <CommandItem
                    key={country.code}
                    value={country.code}
                    keywords={[country.name]}
                    onSelect={(currentValue) => {
                      onChange(currentValue);
                      setOpen(false);
                    }}
                  >
                    <CountryItem country={country} />
                    <CheckIcon
                      className={cn(
                        "size-4",
                        value === country.code ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </CommandItem>
                ))}
              </ScrollArea>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
